import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Target, Eye, Award, TrendingUp } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Helmet>
        <title>Hakkımızda - Ener Yazılım | Dijital Dönüşüm Ortağınız</title>
        <meta name="description" content="Ener Yazılım, yazılım, elektronik, bilişim güvenlik ve teknoloji alanlarında uzmanlaşmış, yenilikçi çözümler sunan bir teknoloji şirketidir." />
        <meta name="keywords" content="hakkımızda, ener yazılım, teknoloji şirketi, misyon, vizyon, değerlerimiz" />
        <link rel="canonical" href="https://www.eneryazilim.com/hakkimizda" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.eneryazilim.com/hakkimizda" />
        <meta property="og:title" content="Hakkımızda - Ener Yazılım" />
        <meta property="og:description" content="Ener Yazılım, yazılım, elektronik, bilişim güvenlik ve teknoloji alanlarında uzmanlaşmış, yenilikçi çözümler sunan bir teknoloji şirketidir." />
        <meta property="og:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.eneryazilim.com/hakkimizda" />
        <meta name="twitter:title" content="Hakkımızda - Ener Yazılım" />
        <meta name="twitter:description" content="Ener Yazılım, yazılım, elektronik, bilişim güvenlik ve teknoloji alanlarında uzmanlaşmış, yenilikçi çözümler sunan bir teknoloji şirketidir." />
        <meta name="twitter:image" content="https://www.eneryazilim.com/logo.png" />
        
        {/* Structured Data - AboutPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "mainEntity": {
              "@type": "Organization",
              "name": "Ener Yazılım Elektronik Bilişim Güvenlik Teknolojileri",
              "url": "https://www.eneryazilim.com",
              "description": "Yazılım, elektronik, bilişim güvenlik ve teknoloji alanlarında uzmanlaşmış teknoloji şirketi"
            }
          })}
        </script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/10 via-transparent to-[#F7931E]/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="about-title">
              <span className="gradient-text">Hakkımızda</span>
            </h1>
            <p className="text-lg text-white/70">
              Ener Yazılım Elektronik Bilişim Güvenlik Teknolojileri San Tic Ltd Şti
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20" data-testid="company-story">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="glass p-8 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold mb-6 gradient-text">Biz Kimiz?</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Ener Yazılım, yazılım, elektronik, bilişim güvenlik ve teknoloji alanlarında uzmanlaşmış, 
                  yenilikçi çözümler sunan bir teknoloji şirketidir. Müşterilerimize en kaliteli hizmeti sunmak için 
                  sürekli olarak kendimizi geliştiriyor ve sektördeki en son teknolojileri takip ediyoruz.
                </p>
                <p>
                  Deneyimli ekibimiz ve güçlü altyapımızla, küçük işletmelerden büyük kuruluşlara kadar her ölçekte 
                  projeye özel çözümler üretiyoruz. Dijital dönüşüm yolculuğunuzda güvenilir bir iş ortağı olarak 
                  yanınızdayız.
                </p>
              </div>
            </div>

            {/* Values */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: Target,
                  title: 'Misyonumuz',
                  description: 'İşletmelerin dijital dönüşümüne öncülük ederek, güvenli ve yenilikçi teknoloji çözümleri sunmak.',
                },
                {
                  icon: Eye,
                  title: 'Vizyonumuz',
                  description: 'Teknoloji sektöründe öncü bir marka olarak, ulusal ve uluslararası alanda tanınan bir şirket olmak.',
                },
                {
                  icon: Award,
                  title: 'Kalite Anlayışımız',
                  description: 'Her projede mükemmeli hedefleyerek, sürekli iyileştirme ve geliştirme anlayışıyla çalışmak.',
                },
                {
                  icon: TrendingUp,
                  title: 'İnovasyon',
                  description: 'Sürekli araştırma ve geliştirme ile sektördeki en yeni teknolojileri projelerimize entegre etmek.',
                },
              ].map((item, index) => (
                <div key={index} className="glass p-6 rounded-2xl card-hover" data-testid={`value-card-${index}`}>
                  <item.icon className="w-12 h-12 text-[#FF6B35] mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-white/60">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;